/**
 * Typing Indicator Component
 * @implements FS-002.7.11 - AI PM Response Delivery
 * @implements US-020 - AI PM Conversation UI
 *
 * Shows a "AI PM is thinking..." indicator while waiting for AI PM response.
 * Uses animated dots to indicate activity.
 */

import { Box, Typography, Avatar, keyframes } from '@mui/material';
import { SmartToy as AiIcon } from '@mui/icons-material';

/**
 * Props for TypingIndicator component
 */
export interface TypingIndicatorProps {
  /** Whether the indicator is visible */
  isVisible?: boolean;
}

/**
 * Bounce animation for dots
 */
const bounce = keyframes`
  0%, 80%, 100% {
    transform: translateY(0);
  }
  40% {
    transform: translateY(-6px);
  }
`;

/**
 * TypingIndicator Component
 * @implements FS-002.7.11 - "AI PM is thinking..." shown while response is generating
 *
 * Displays an animated typing indicator styled to match AI PM messages.
 *
 * @example
 * ```tsx
 * <TypingIndicator isVisible={isWaitingForResponse} />
 * ```
 */
export const TypingIndicator = ({ isVisible = true }: TypingIndicatorProps) => {
  if (!isVisible) {
    return null;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        gap: 1.5,
        mb: 2,
        maxWidth: '100%',
      }}
    >
      {/* AI Avatar */}
      <Avatar
        sx={{
          bgcolor: 'secondary.main',
          width: 36,
          height: 36,
          flexShrink: 0,
        }}
      >
        <AiIcon fontSize="small" />
      </Avatar>

      {/* Indicator Content */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
        }}
      >
        {/* Label */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mb: 0.5, px: 1 }}
        >
          AI Project Manager
        </Typography>

        {/* Typing Bubble */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            py: 1.5,
            backgroundColor: 'grey.100',
            borderRadius: 2,
            borderTopLeftRadius: 0,
          }}
        >
          {/* Animated Dots */}
          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            {[0, 1, 2].map((index) => (
              <Box
                key={index}
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  backgroundColor: 'secondary.main',
                  animation: `${bounce} 1.4s ease-in-out infinite`,
                  animationDelay: `${index * 0.16}s`,
                }}
              />
            ))}
          </Box>

          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ ml: 0.5, fontStyle: 'italic' }}
          >
            thinking...
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
